import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '_store';
import * as api from './backupsApi';
import { BackupRouter } from './BackupRouter';
import {
    DeliveryBadge, Empty, ErrorBanner, Loading, commitLabel, formatTime, timeAgo,
} from './backupsUi';
import './backups.css';

export { Backups };

function Backups() {
    const { selectedOrganization, userRole, authUser } = useContext(AuthContext);
    const isAdmin = userRole === 'admin';
    const [routers, setRouters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);

    const multiOrg = (authUser?.organizations?.length || 0) > 1;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.fetchRouters(selectedOrganization);
            setRouters(data.routers || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedOrganization]);

    useEffect(() => { load(); }, [load]);

    // Back to the list whenever the organization changes.
    useEffect(() => { setSelected(null); }, [selectedOrganization]);

    const counts = useMemo(() => {
        const c = { acknowledged: 0, attention: 0 };
        routers.forEach((r) => {
            if (r.delivery === 'acknowledged') c.acknowledged += 1;
            if (['conflict', 'failed', 'refused'].includes(r.delivery)) c.attention += 1;
        });
        return c;
    }, [routers]);

    if (selected) {
        return (
            <BackupRouter
                routerId={selected}
                isAdmin={isAdmin}
                onBack={() => { setSelected(null); load(); }}
            />
        );
    }

    return (
        <div className="bk-page">
            <div className="bk-hero">
                <div>
                    <div className="bk-breadcrumb">
                        <span className="bk-breadcrumb-dot" />
                        <span className="bk-breadcrumb-text">CONFIG_BACKUPS</span>
                    </div>
                    <h1 className="bk-title">Backups</h1>
                </div>
                <div className="bk-hero-actions">
                    {!loading && routers.length > 0 && (
                        <>
                            <span className="bk-badge bk-badge-ok">
                                <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
                                {counts.acknowledged} delivered
                            </span>
                            {counts.attention > 0 && (
                                <span className="bk-badge bk-badge-error">
                                    <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                                    {counts.attention} need attention
                                </span>
                            )}
                        </>
                    )}
                    <button type="button" className="bk-btn" onClick={load} disabled={loading}>
                        <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
                        Refresh
                    </button>
                </div>
            </div>

            <ErrorBanner msg={error} onClose={() => setError(null)} />

            <div className="bk-card">
                <div className="bk-card-header">
                    <h2 className="bk-card-title">Routers</h2>
                    <span className="bk-card-meta">
                        Each configuration change on a router is kept as one commit. Contents include secrets; every view is audited.
                    </span>
                </div>
                {loading ? (
                    <Loading label="Loading routers" />
                ) : routers.length === 0 ? (
                    <Empty icon="router" title="No routers in this organization">
                        Routers appear once their netc-agent connects to this server.
                    </Empty>
                ) : (
                    <div className="bk-table-wrap">
                        <table className="bk-table">
                            <thead>
                                <tr>
                                    <th className="bk-th">Router</th>
                                    {multiOrg && <th className="bk-th">Organization</th>}
                                    <th className="bk-th">Last backup</th>
                                    <th className="bk-th">Change</th>
                                    <th className="bk-th bk-num">Commits</th>
                                    <th className="bk-th">Delivery</th>
                                    <th className="bk-th">Agent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {routers.map((r) => (
                                    <tr
                                        key={r.router_id}
                                        className="bk-row"
                                        tabIndex={0}
                                        onClick={() => setSelected(r.router_id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelected(r.router_id);
                                            }
                                        }}
                                        aria-label={`Open backups of ${r.router_id}`}
                                    >
                                        <td className="bk-td">
                                            <div className="bk-row-name bk-mono">{r.router_id}</div>
                                            {r.hostname && r.hostname !== r.router_id && (
                                                <div className="bk-row-sub">{r.hostname}</div>
                                            )}
                                        </td>
                                        {multiOrg && <td className="bk-td">{r.organization_name || r.organization_id}</td>}
                                        <td className="bk-td" title={formatTime(r.last_commit?.committed_at)}>
                                            {r.last_commit ? timeAgo(r.last_commit.committed_at) : '—'}
                                        </td>
                                        <td className="bk-td">
                                            {r.last_commit ? (
                                                <span className="bk-mono">{commitLabel(r.last_commit)}</span>
                                            ) : '—'}
                                        </td>
                                        <td className="bk-td bk-num">{r.commit_count || 0}</td>
                                        <td className="bk-td"><DeliveryBadge delivery={r.delivery} /></td>
                                        <td className="bk-td">
                                            <span className={`bk-dot ${r.connected ? 'on' : 'off'}`} aria-hidden="true" />
                                            {r.connected ? 'Online' : 'Offline'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
